import { Analytics } from '@cobliteam/analytics';
import { isDeviceTypeCamera, type TrackingDevice } from '@cobliteam/api-lib';

import { Location } from '@cobliteam/common-types';
import { withPermissions } from '@cobliteam/permissions';
import { Button, Tooltip, Typography } from '@cobliteam/ui/components';
import Switch from '@cobliteam/ui/components/Switch';
import Icon from '@cobliteam/ui/icons';
import type React from 'react';

import MapMatch from './components/MapMatch/MapMatch';
import NoAccessHover from './components/NoAccessHover/NoAccessHover';

export interface EventToggleProps {
  roadSpeedEventActive: boolean;
  vehicleSpeedEventActive: boolean;
  geofencesActive: boolean;
  geofencesEventsActive: boolean;
  mediaEventsActive: boolean;
  mapMatchActive: boolean;
  fleetHasCamera: boolean;
  selectedDevice?: TrackingDevice;
  onToggleRoadSpeedEvents: (active: boolean) => void;
  onToggleVehicleSpeedEvents: (active: boolean) => void;
  onToggleGeofences: (active: boolean) => void;
  onToggleGeofencesEvents: (active: boolean) => void;
  onToggleMediaEvents: (active: boolean) => void;
  onToggleMapMatch: (active: boolean) => void;
}

export const EventToggle: React.FC<EventToggleProps> = ({
  roadSpeedEventActive,
  vehicleSpeedEventActive,
  geofencesActive,
  mediaEventsActive,
  geofencesEventsActive,
  mapMatchActive,
  fleetHasCamera,
  selectedDevice,
  onToggleRoadSpeedEvents,
  onToggleVehicleSpeedEvents,
  onToggleGeofences,
  onToggleMediaEvents,
  onToggleGeofencesEvents,
  onToggleMapMatch,
}) => {
  const handleToggleRoadSpeedEvents = () => onToggleRoadSpeedEvents(!roadSpeedEventActive);
  const handleToggleVehicleSpeedEvents = () => onToggleVehicleSpeedEvents(!vehicleSpeedEventActive);
  const handleToggleGeofences = () => onToggleGeofences(!geofencesActive);
  const handleToggleGeofencesEvents = () => onToggleGeofencesEvents(!geofencesEventsActive);
  const handleToggleMediaEvents = () => onToggleMediaEvents(!mediaEventsActive);
  const handleToggleMapMatch = () => onToggleMapMatch(!mapMatchActive);

  const handleCtaClick = () => {
    Analytics.track('Know More About Cobli Cam Clicked', {
      origin: 'tracking page toggle tooltip',
    });

    window.open(fleetHasCamera ? `${Location.VEHICLES}?upsell_modal=true` : Location.CAMERA_LP);
  };

  const RoadSpeedEventsToggle = withPermissions({
    Component: () => (
      <div className='flex items-center gap-2'>
        <Switch size='sm' className='shrink-0' name='RoadSpeedEventsSwitch' onCheckedChange={handleToggleRoadSpeedEvents} checked={roadSpeedEventActive} />
        <Typography variant='caption-default'>Velocidade da via excedida</Typography>
      </div>
    ),
    permissions: 'view:fleet-safety',
  });

  const VehicleSpeedEventToggle = withPermissions({
    Component: () => (
      <div className='flex items-center gap-2'>
        <Switch size='sm' className='shrink-0' name='VehicleSpeedEventSwitch' onCheckedChange={handleToggleVehicleSpeedEvents} checked={vehicleSpeedEventActive} />
        <Typography variant='caption-default'>Velocidade do veículo excedida</Typography>
      </div>
    ),
    permissions: 'view:fleet-safety',
  });

  const GeofencesToggle = withPermissions({
    Component: () => (
      <div className='flex items-center gap-2'>
        <Switch size='sm' className='shrink-0' name='GeofencesSwitch' onCheckedChange={handleToggleGeofences} checked={geofencesActive} />
        <Typography variant='caption-default'>Locais de interesse</Typography>
      </div>
    ),
    permissions: 'view:fleet-safety',
  });

  const GeofencesEventsToggle = withPermissions({
    Component: () => (
      <div className='flex gap-2'>
        <Switch size='sm' className='shrink-0' name='GeofencesEventsSwitch' onCheckedChange={handleToggleGeofencesEvents} checked={geofencesEventsActive} />
        <Typography variant='caption-default'>Entrada e saída de locais de interesse</Typography>
      </div>
    ),
    permissions: 'view:fleet-safety',
    fallback: () => <NoAccessHover />,
  });

  const MediaEventsToggle = withPermissions({
    Component: () => {
      const showMediaRequestOption = !!(fleetHasCamera && selectedDevice && isDeviceTypeCamera(selectedDevice.type));
      const tooltipContent = showMediaRequestOption
        ? 'São vídeos gerados automaticamente pela câmera quando o motorista comete eventos de risco durante a condução.'
        : 'Exclusivo CobliCam, adquira já';

      const tooltipLabel = (
        <Tooltip content={tooltipContent}>
          <Button size='sm' icon={<Icon name='video' color='gray' />} variant='ghost' className='h-fit w-fit' onClick={handleCtaClick} />
        </Tooltip>
      );

      return (
        <div className='flex items-center gap-2'>
          <Switch
            size='sm'
            className='shrink-0'
            name='MediaEventsSwitch'
            onCheckedChange={handleToggleMediaEvents}
            checked={mediaEventsActive && showMediaRequestOption}
            disabled={!showMediaRequestOption}
          />
          <div className='flex items-center gap-2'>
            <Typography variant='caption-default'>Eventos com vídeo</Typography>
            {tooltipLabel}
          </div>
        </div>
      );
    },
    permissions: 'view:fleet-safety',
  });

  return (
    <div className='flex min-w-[240px] flex-col gap-2.5'>
      <RoadSpeedEventsToggle />
      <VehicleSpeedEventToggle />
      <GeofencesToggle />
      <MediaEventsToggle />
      <GeofencesEventsToggle />
      <MapMatch handleToggleMapMatch={handleToggleMapMatch} mapMatchActive={mapMatchActive} />
    </div>
  );
};
